import { useEffect, useState } from 'react'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import DataTable from '../components/ui/DataTable'
import EmptyState from '../components/ui/EmptyState'
import FormField from '../components/ui/FormField'
import LoadingState from '../components/ui/LoadingState'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import receiptImageAssetsService from '../services/receiptImageAssetsService'
import { ui } from '../styles/ui'
import { formatDateTime } from '../utils/date'

export default function ReceiptImageAssetsAdmin() {
  const [stats, setStats] = useState({ assets_count: 0, stages_count: 0 })
  const [items, setItems] = useState([])
  const [page, setPage] = useState(1)
  const [perPage] = useState(25)
  const [receiptId, setReceiptId] = useState('')
  const [stage, setStage] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    receiptImageAssetsService.adminStats().then((res) => setStats(res.data || {})).catch(() => setStats({ assets_count: 0, stages_count: 0 }))
  }, [])

  useEffect(() => {
    setLoading(true)
    receiptImageAssetsService.adminList({
      page,
      per_page: perPage,
      receipt_id: receiptId || undefined,
      stage: stage || undefined,
    }).then((res) => {
      setItems(res.data.items || [])
    }).finally(() => setLoading(false))
  }, [page, perPage, receiptId, stage])

  return (
    <PageShell>
      <PageHeader title="Receipt Image Assets" />

      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div><div className={ui.forms.label}>Assets</div><div className="font-semibold">{stats.assets_count ?? 0}</div></div>
          <div><div className={ui.forms.label}>Stages</div><div className="font-semibold">{stats.stages_count ?? 0}</div></div>
          <FormField label="Receipt ID" htmlFor="receipt-id">
            <input id="receipt-id" className={ui.input} value={receiptId} onChange={(e) => { setPage(1); setReceiptId(e.target.value) }} />
          </FormField>
          <FormField label="Stage" htmlFor="asset-stage">
            <input id="asset-stage" className={ui.input} value={stage} onChange={(e) => { setPage(1); setStage(e.target.value) }} />
          </FormField>
        </div>
      </Card>

      {loading ? (
        <LoadingState text="Loading assets..." />
      ) : items.length === 0 ? (
        <EmptyState title="No assets" description="No receipt image assets matched current filters." />
      ) : (
        <DataTable
          columns={[
            { key: 'preview', label: 'Preview' },
            { key: 'receipt', label: 'Receipt', sortable: true, sortAccessor: (item) => item.receiptId || '' },
            { key: 'stage', label: 'Stage', sortable: true },
            { key: 'mime', label: 'Mime', sortable: true, sortAccessor: (item) => item.mimeType || '' },
            { key: 'size', label: 'Size', sortable: true, sortAccessor: (item) => Number(item.sizeBytes || 0) },
            { key: 'created', label: 'Created', sortable: true, sortAccessor: (item) => item.createdAt || '' },
          ]}
          rows={items}
          renderRow={(item) => (
            <tr key={item.id} className={ui.tableRow}>
              <td className={ui.tableCell}>
                <img
                  src={receiptImageAssetsService.adminAssetImageUrl(item.id)}
                  alt={item.stage}
                  className="h-16 w-16 object-contain rounded border border-outline"
                />
              </td>
              <td className={ui.tableCellText}>{item.receiptId}</td>
              <td className={ui.tableCellText}>{item.stage}</td>
              <td className={ui.tableCellText}>{item.mimeType}</td>
              <td className={ui.tableCellText}>{item.sizeBytes}</td>
              <td className={ui.tableCellText}>{item.createdAt ? formatDateTime(item.createdAt) : '-'}</td>
            </tr>
          )}
        />
      )}

      <div className="flex items-center gap-2">
        <Button variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
        <span className="text-sm text-muted-foreground">Page {page}</span>
        <Button variant="ghost" disabled={items.length < perPage} onClick={() => setPage((p) => p + 1)}>Next</Button>
      </div>
    </PageShell>
  )
}

